import React, { Component } from 'react';
import firebase from '../firebase.js';
import BlurArea from '../Components/BlurArea.js';
import Loading from './Loading.js';
import RoomHeader from './RoomHeader.js';
import ConnectedUsers from './ConnectedUsers.js';
import JoinRoomPrompt from './JoinRoomPrompt.js';
import TeamEstimateTracker from './TeamEstimateTracker.js';
import update from 'immutability-helper';

export default class extends Component {
  constructor(props) {
    super(props);
    this.state = {
      room: {
        name: this.props.match.params.id
      },
      users: [],
      roomLoading: true,
      showJoinRoomPrompt: true
    }

    this.onAddUser = this.onAddUser.bind(this);
    this.onRoomUpdateStepEstimate = this.onRoomUpdateStepEstimate.bind(this);
    this.onRoomNextStep = this.onRoomNextStep.bind(this);
    this.onRoomEstimatesReset = this.onRoomEstimatesReset.bind(this);
    this.onRoomRestart = this.onRoomRestart.bind(this);
  }

  componentWillMount() {
    this.signInUser();
    this.setupRoom();
  }

  componentWillUpdate(nextProps, nextState) {
    if (nextState.room.steps !== this.state.room.steps) {
      this.determineCurrentRoomStep(nextState);
      this.determineTeamEstimateReady(nextState);
    }

    if (nextState.users !== this.state.users) {
      this.determineCurrentUser(nextState);
      this.determineUsersReady(nextState);
      this.determineLastUserReady(nextState);
    }

    if (nextState.roomShowEstimate !== this.state.roomShowEstimate)
      this.determineCurrentRoomStepEstimate(nextState);

    if (nextState.room.total !== this.state.room.total)
      this.determineTotalEstimate(nextState);

    if (this.state.showJoinRoomPrompt && this.state.currentUser)
      this.hideJoinRoomPrompt();
  }

  defaultRoom(roomName) {
    return {
      name: roomName,
      steps: [
        { type: 'optimistic', status: 'in-progress', estimate: '' },
        { type: 'realistic', status: 'disabled', estimate: '' },
        { type: 'pessimistic', status: 'disabled', estimate: '' }
      ]
    }
  }

  defaultUser(authUser, userDisplayName) {
    return {
      uid: authUser.uid,
      name: userDisplayName,
      estimate: '',
      status: 'in-progress'
    }
  }

  onRoomUpdateStepEstimate(currentStepEstimate) {
    let currentRoomStepIndex;
    let roomSteps = this.state.room.steps;
    let currentRoomStep = roomSteps.find((step, index) => {
      currentRoomStepIndex = index;
      return step.type === this.state.currentRoomStep.type;
    });
    this.updateRoomSteps(roomSteps, currentRoomStep, currentRoomStepIndex, currentStepEstimate)
  }

  onRoomNextStep() {
    let currentRoomStepIndex;
    let roomSteps = this.state.room.steps;
    let currentRoomStep = roomSteps.find((step, index) => {
      currentRoomStepIndex = index;
      return step.type === this.state.currentRoomStep.type;
    });
    let nextRoomStepIndex = currentRoomStepIndex + 1;

    if (this.state.room.steps.length > nextRoomStepIndex) {
      let currentRoomStepStatus = { status: 'showing' };
      let nextRoomStepStatus = { status: 'in-progress' };
      let nextRoomStep = this.state.room.steps[nextRoomStepIndex];
      let newCurrentRoomStep = update(currentRoomStep, {$merge: currentRoomStepStatus});
      let newNextRoomStep = update(nextRoomStep, {$merge: nextRoomStepStatus});
      let newRoomSteps = update(roomSteps, {[currentRoomStepIndex]: {$merge: newCurrentRoomStep}});
          newRoomSteps = update(newRoomSteps, {[nextRoomStepIndex]: {$merge: newNextRoomStep}});
      this.updateRoom({ steps: newRoomSteps });
      this.clearUsersEstimates();
    } else if (this.state.roomStepsReady) {
      this.calculateTotalEstimate(this.state.room);
    }
  }

  onRoomEstimatesReset() {
    this.clearUsersEstimates();
  }

  onRoomRestart() {
    let roomName = this.state.room.name;
    let newRoom = this.defaultRoom(roomName);
    this.clearUsersEstimatesAndSetRoom(newRoom);
  }

  onAddUser(userDisplayName) {
    let context = this;
    firebase.auth().onAuthStateChanged(function(authUser) {
      if (authUser) {
        let usersRef = context.state.roomRef.collection('users');
        let newUser = context.defaultUser(authUser, userDisplayName);
        context.addUser(usersRef, newUser);
        context.hideJoinRoomPrompt();
      } else {
        context.signInUser()
      }
    });
  }

  setupRoom() {
    let roomsRef = firebase.firestore().collection('rooms');
    let roomName = this.state.room.name;
    let context = this;

    roomsRef.where('name', '==', roomName).limit(1).onSnapshot(function(rooms) {
      let room = rooms.docs[0];
      if (room) {
        let roomRef = room.ref;
        context.setState({
          roomRef: roomRef,
          room: room.data()
        });
        context.getUsersSnapshot(roomRef);
      } else {
        let newRoom = context.defaultRoom(roomName);
        context.addRoom(roomsRef, newRoom);
      }
    });
  }

  getUsersSnapshot(roomRef) {
    let context = this;
    roomRef.collection('users').onSnapshot(function(querySnapshot) {
      let users = [];
      querySnapshot.forEach((user) => {
        let userObject = update(user.data(), {$merge: { id: user.id }});
        users = [userObject].concat(users);
      });
      context.setState({ users: users });
      context.roomLoaded();
    });
  }

  determineCurrentRoomStep(nextState) {
    let currentRoomStepInProgress = nextState.room.steps.find(roomStep => roomStep.status === 'in-progress');
    let currentRoomStepDeciding = nextState.room.steps.find(roomStep => roomStep.status === 'deciding');
    if (currentRoomStepInProgress) {
      this.setState({ currentRoomStep: currentRoomStepInProgress });
    } else if (currentRoomStepDeciding) {
      this.setState({ currentRoomStep: currentRoomStepDeciding });
    }
  }

  determineCurrentRoomStepEstimate(nextState) {
    if (nextState.currentRoomStep) {
      let currentRoomStepIndex;
      let roomSteps = nextState.room.steps;

      let usersEstimates = nextState.users.map(user => user.estimate).filter(estimate => /^\d+$/.test(estimate));
      let usersSkips = nextState.users.map(user => user.estimate).filter(estimate => estimate === '?');

      let currentRoomStep = roomSteps.find((step, index) => {
        currentRoomStepIndex = index;
        return step.type === nextState.currentRoomStep.type;
      });

      if (nextState.roomShowEstimate && !nextState.currentRoomStep.estimate) {
        if (usersEstimates.length > 0) {
          let currentStepEstimate = this.calculateUsersEstimates(usersEstimates);
          this.updateRoomSteps(roomSteps, currentRoomStep, currentRoomStepIndex, currentStepEstimate)
        } else if (usersSkips.length > 0) {
          let currentStepEstimate = { estimate: '?', status: 'deciding' };
          this.updateRoomSteps(roomSteps, currentRoomStep, currentRoomStepIndex, currentStepEstimate)
        }
      } else if (!nextState.roomShowEstimate) {
        let currentStepEstimate = { estimate: '', status: 'in-progress' };
        this.updateRoomSteps(roomSteps, currentRoomStep, currentRoomStepIndex, currentStepEstimate)
      }
    }
  }

  determineTeamEstimateReady(nextState) {
    if (nextState.room) {
      if (nextState.room.steps.length > 0) {
        let stepsReady = nextState.room.steps.every(step => (step.status === 'showing' || step.status === 'deciding'));
        if (stepsReady) {
          this.setState({ roomStepsReady: true })
        } else {
          this.setState({ roomStepsReady: false })
        }
      }
    }
  }

  determineCurrentUser(nextState) {
    let users = nextState.users;
    if (users.length > 0) {
      let authUser = firebase.auth().currentUser;
      if (authUser) {
        let userMatch = users.find(localUser => localUser.uid === authUser.uid);
        if (userMatch)
          this.setState({ currentUser: userMatch });
      }
    }
  }

  determineUsersReady(nextState) {
    if (nextState.users.length > 0) {
      if (nextState.users.every(user => user.status === 'ready')) {
        this.setState({ roomShowEstimate: true });
      } else {
        this.setState({ roomShowEstimate: false });
      }
    }
  }

  determineLastUserReady(nextState) {
    if (nextState.users.length > 0) {
      let remainingUsers = nextState.users.filter(user => user.status !== 'ready');
      let authUser = firebase.auth().currentUser;
      if (authUser) {
        let userMatch = remainingUsers.find(localUser => localUser.uid === authUser.uid);
        if (remainingUsers.length === 1 && userMatch) {
          this.setState({ roomLastUserReady: true });
        } else {
          this.setState({ roomLastUserReady: false });
        }
      }
    }
  }

  determineTotalEstimate(nextState) {
    if (nextState.room.total) {
      this.setState({ roomShowTotalEstimate: true });
      this.clearUsersEstimates();
    } else {
      this.setState({ roomShowTotalEstimate: false });
    }
  }

  calculateUsersEstimates(usersEstimates) {
    let usersEstimatesTotal = 0;
    usersEstimates.forEach(estimate => { usersEstimatesTotal += Number.parseInt(estimate, 10); });
    return { estimate: Math.ceil(usersEstimatesTotal / usersEstimates.length), status: 'deciding' };
  }

  calculateTotalEstimate(room) {
    let optimistic = room.steps.find(step => step.type === 'optimistic');
    let realistic = room.steps.find(step => step.type === 'realistic');
    let pessimistic = room.steps.find(step => step.type === 'pessimistic');
    let totalEstimate = this.plusStandardDeviation(optimistic.estimate, realistic.estimate, pessimistic.estimate);
    let newRoom = update(room, {$merge: {total: totalEstimate}});
    this.setState({ room: newRoom });
    this.updateRoom({ total: totalEstimate });
  }

  weightedMean(optimistic, realistic, pessimistic) {
    return ((+optimistic) + 4 * (+realistic) + (+pessimistic)) / 6.0;
  };

  plusStandardDeviation(optimistic, realistic, pessimistic) {
    let weightedMeanEstimate = this.weightedMean(optimistic, realistic, pessimistic);
    let unroundedWeightedMean = weightedMeanEstimate + 2 * (((+pessimistic) - (+optimistic)) / 6.0);
    return Math.round(unroundedWeightedMean / 0.25) * 0.25;
  };

  roomLoaded() {
    this.setState({ roomLoading: false });
  }

  addUser(usersRef, newUser) {
    usersRef.add(newUser).catch(function(error) {
      console.error("Error adding user: ", error);
    });
  }

  addRoom(roomsRef, newRoom) {
    roomsRef.add(newRoom).catch(function(error) {
      console.error("Error adding room: ", error);
    });
  }

  updateRoom(room) {
    this.state.roomRef.update(room).catch(function(error) {
      console.error("Error updating document: ", error);
    });
  }

  updateRoomSteps(roomSteps, currentRoomStep, currentRoomStepIndex, currentStepEstimate) {
    let newCurrentRoomStep = update(currentRoomStep, {$merge: currentStepEstimate});
    let newRoomSteps = update(roomSteps, {[currentRoomStepIndex]: {$merge: newCurrentRoomStep}});
    this.updateRoom({ steps: newRoomSteps });
  }

  clearUsersEstimates() {
    if (this.state.roomRef) {
      let firestore = firebase.firestore();
      let batch = firestore.batch();
      let usersRef = this.state.roomRef.collection('users');
      let users = this.state.users.map(user => user.id );
      users.forEach(userID => {
        let ref = usersRef.doc(userID);
        batch.update(ref, { estimate: '', status: 'in-progress' });
      });
      batch.commit();
    }
  }

  clearUsersEstimatesAndSetRoom(newRoom) {
    let roomRef = this.state.roomRef;
    if (roomRef) {
      let firestore = firebase.firestore();
      let batch = firestore.batch();
      let usersRef = this.state.roomRef.collection('users');
      let users = this.state.users.map(user => user.id );
      users.forEach(userID => {
        let ref = usersRef.doc(userID);
        batch.update(ref, { estimate: '', status: 'in-progress' });
      });
      batch.set(roomRef, newRoom)
      batch.commit();
    }
  }

  signInUser() {
    firebase.auth().signInAnonymously().catch(function(error) {
      console.error("Error signing in as guest")
    });
  }

  hideJoinRoomPrompt() {
    if (this.state.showJoinRoomPrompt)
      this.setState({ showJoinRoomPrompt: false });
  }

  render() {
    if (this.state.roomLoading) {
      return ( <Loading /> );
    } else {
      let joinRoomPrompt = (this.state.showJoinRoomPrompt) ?
        <JoinRoomPrompt onAddUser={this.onAddUser} /> : null;

      return (
        <div className="team-room">
          {joinRoomPrompt}
          <BlurArea active={this.state.showJoinRoomPrompt}>
            <RoomHeader roomName={this.state.room.name} />

            <TeamEstimateTracker
              room={this.state.room}
              roomStepsReady={this.state.roomStepsReady}
              roomShowEstimate={this.state.roomShowEstimate}
              roomShowTotalEstimate={this.state.roomShowTotalEstimate}
              onRoomUpdateStepEstimate={this.onRoomUpdateStepEstimate}
              onRoomNextStep={this.onRoomNextStep}
              onRoomEstimatesReset={this.onRoomEstimatesReset}
              onRoomRestart={this.onRoomRestart} />

            <ConnectedUsers
              roomRef={this.state.roomRef}
              roomShowEstimate={this.state.roomShowEstimate}
              roomShowTotalEstimate={this.state.roomShowTotalEstimate}
              roomCurrentStepType={this.state.currentRoomStep.type}
              roomLastUserReady={this.state.roomLastUserReady}
              users={this.state.users}
              currentUser={this.state.currentUser}
              showJoinRoomPrompt={this.state.showJoinRoomPrompt} />
          </BlurArea>
        </div>
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/Room/Room.js